<template>
  <section class="section">
    <div class="container">
      <h1 class="title is-1">{{ title }}</h1>
      <div v-if="loadingError.length > 0">
        <div class="columns">
          <div class="column is-12">
            <p>{{loadingError}}</p>
          </div>
        </div>
        <div class="columns">
          <div class="column is-12">
            <router-link :to="{ name: 'manage' }" class="button">Go back</router-link>
          </div>
        </div>
      </div>
      <div v-else-if="isLoadingClublinks">
        <div class="columns">
          <div class="column is-12">
            <p>Loading clublinks</p>
          </div>
        </div>
      </div>
      <div v-else>
        <form novalidate @submit.prevent="create">
          <h3 class="title is-3">Club</h3>
          <b-field label="Name" label-for="name" :type="getFieldType('name')" :message="getError('name')">
            <b-input :disabled="isLoading" v-model="clublink.name" v-validate="{ required: true, min: 1, max: 255 }" maxlength="255" required name="name" id="name" />
          </b-field>
          <b-field label="Zone" label-for="zone" :type="getFieldType('zone')" :message="getError('zone')">
            <b-input :disabled="isLoading" v-model="clublink.zone" v-validate="{ min: 1, max: 255 }" maxlength="255" name="zone" id="zone" />
          </b-field>
          <b-field label="City" label-for="city" :type="getFieldType('city')" :message="getError('city')">
            <b-input :disabled="isLoading" v-model="clublink.city" v-validate="{ min: 1, max: 255 }" maxlength="255" name="city" id="city" />
          </b-field>
          <b-field label="Primary color" label-for="color-primary" :type="getFieldType('primary color')" :message="getError('primary color')">
            <b-input :disabled="isLoading" type="color" v-model="clublink.colors.primary" v-validate="{ length: 7, regex: /^#[0-9a-f]{6}$/i }" name="primary color" id="color-primary" />
          </b-field>
          <b-field label="Secondary color" label-for="color-secondary" :type="getFieldType('secondary color')" :message="getError('secondary color')">
            <b-input :disabled="isLoading" type="color" v-model="clublink.colors.secondary" v-validate="{ length: 7, regex: /^#[0-9a-f]{6}$/i }" name="secondary color" id="color-secondary" />
          </b-field>
          <b-field label="Emblem url" label-for="emblem" :type="getFieldType('emblem url')" :message="getError('emblem url')">
            <b-input :disabled="isLoading" type="url" v-model="clublink.emblem" v-validate="{ url: {require_protocol: true }, min: 1, max: 1023 }" maxlength="1023" name="emblem url" id="emblem" />
          </b-field>
          <div>
            <h3 class="title is-3">Players</h3>
            <div v-for="(player, index) in clublink.players" :key="player.key" class="box">
              <h4 class="title is-4">Player #{{index + 1}}</h4>
              <b-field label="Login" label-for="player-login" :type="getFieldType('player login')" :message="getError('player login')">
                <b-input :disabled="isLoading" v-model="player.login" v-validate="{ required: true, min: 1, max: 255 }" maxlength="255" required name="player login" id="player-login" />
              </b-field>
              <b-field label="Nickname" label-for="player-nickname" :type="getFieldType('player nickname')" :message="getError('player nickname')">
                <b-input :disabled="isLoading" v-model="player.nickname" v-validate="{ min: 1, max: 255 }" maxlength="255" name="player nickname" id="player-nickname" />
              </b-field>
              <b-field label="Avatar url" label-for="player-avatar" :type="getFieldType('player avatar url')" :message="getError('player avatar url')">
                <b-input :disabled="isLoading" type="url" v-model="player.avatar" v-validate="{ url: {require_protocol: true }, min: 1, max: 1023 }" maxlength="1023" name="player avatar url" id="player-avatar" />
              </b-field>
              <div>
                <b-button :disabled="isLoading" type="is-warning" class="is-fullwidth" outlined @click="removePlayer(player.key)">Remove player</b-button>
              </div>
            </div>
            <div class="columns">
              <div class="column is-12">
                <b-button type="is-primary" class="is-fullwidth" outlined @click="addPlayer" :disabled="playersLimitReached || isLoading">{{addPlayerBtn}}</b-button>
              </div>
            </div>
          </div>
          <div>
            <div class="spacer"></div>
            <h3 class="title is-3">Sponsors</h3>
            <div v-for="(sponsor, index) in clublink.sponsors" :key="sponsor.key" class="box">
              <h4 class="title is-4">Sponsor #{{index + 1}}</h4>
              <b-field label="Name" label-for="sponsor-name" :type="getFieldType('sponsor name')" :message="getError('sponsor name')">
                <b-input :disabled="isLoading" v-model="sponsor.name" v-validate="{ min: 1, max: 255 }" maxlength="255" name="sponsor name" id="sponsor-name" />
              </b-field>
              <b-field label="Logo url" label-for="sponsor-image" :type="getFieldType('sponsor logo url')" :message="getError('sponsor logo url')">
                <b-input :disabled="isLoading" type="url" v-model="sponsor.image" v-validate="{ required: true, url: {require_protocol: true }, min: 1, max: 1023 }" maxlength="1023" required name="sponsor logo url" id="sponsor-image" />
              </b-field>
              <div>
                <b-button :disabled="isLoading" type="is-warning" class="is-fullwidth" outlined @click="removeSponsor(sponsor.key)">Remove sponsor</b-button>
              </div>
            </div>
            <div class="columns">
              <div class="column is-12">
                <b-button type="is-primary" class="is-fullwidth" outlined @click="addSponsor" :disabled="sponsorsLimitReached || isLoading">{{addSponsorBtn}}</b-button>
              </div>
            </div>
          </div>
          <div class="spacer"></div>
          <div v-if="errors.length > 0 || validationErrors.all().length > 0" class="content">
            <h2 class="title is-2">Errors</h2>
            <ul>
              <li v-for="(error, index) in errors" :key="index">
                {{error}}
              </li>
              <li v-for="(error, index) in validationErrors.all()" :key="index + errors.length">
                {{error}}
              </li>
            </ul>
          </div>
          <div class="spacer"></div>
          <div class="columns">
            <div class="column is-6">
              <b-button :loading="isLoading" native-type="submit" type="is-primary" class="is-fullwidth">{{buttonText}}</b-button>
            </div>
            <div class="column is-6">
              <b-button :disabled="isLoading" type="is-danger" class="is-fullwidth" @click="cancel">Cancel</b-button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { functions, auth } from '@/firebase.js'
import { PLAYERS_LIMIT, SPONSORS_LIMIT, routes } from '@/constants.js'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'

export default {
  name: 'create',
  props: {
    id: String
  },
  data () {
    return {
      clublink: {
        id: '',
        name: '',
        zone: '',
        city: '',
        colors: {
          primary: '#ffffff',
          secondary: '#ffffff'
        },
        emblem: '',
        players: [],
        sponsors: []
      },
      originalClublink: {},
      isLoading: false,
      canLeave: false,
      errors: [],
      loadingError: '',
      key: 0,
      playersLimit: PLAYERS_LIMIT,
      sponsorsLimit: SPONSORS_LIMIT
    }
  },
  computed: {
    isEditing () {
      return typeof this.id === 'string' && this.id !== ''
    },
    buttonText () {
      if (this.isEditing) {
        return this.isLoading ? 'Saving ...' : 'Save'
      } else {
        return this.isLoading ? 'Creating ...' : 'Create'
      }
    },
    title () {
      return this.isEditing ? 'Edit' : 'Create'
    },
    playersLimitReached () {
      return this.clublink.players.length >= this.playersLimit
    },
    sponsorsLimitReached () {
      return this.clublink.sponsors.length >= this.sponsorsLimit
    },
    addPlayerBtn () {
      return `Add player (${this.clublink.players.length}/${this.playersLimit})`
    },
    addSponsorBtn () {
      return `Add sponsor (${this.clublink.sponsors.length}/${this.sponsorsLimit})`
    },
    isLoadingClublinks () {
      return this.$store.state.isLoadingClublinks
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.clublink.id = ''
    if (this.isEditing && !this.isLoadingClublinks) {
      this.clublink.id = to.params.id
      this.loadClublink(this.clublink.id)
    }
    next()
  },
  beforeRouteLeave (to, from, next) {
    let confirm = true
    if (!this.canLeave && !isEqual(this.clublink, this.originalClublink)) {
      confirm = window.confirm('The clublink was modified, do you really want to cancel?')
    }
    if (confirm) {
      next()
    } else {
      next(false)
    }
  },
  created () {
    this.clublink.id = ''
    if (this.isEditing && !this.isLoadingClublinks) {
      this.clublink.id = this.id
      this.loadClublink(this.clublink.id)
    } else {
      this.originalClublink = cloneDeep(this.clublink)
    }
  },
  watch: {
    isLoadingClublinks: function (oldValue, newValue) {
      // Load clublinks once they finish loaded
      if (oldValue === false && newValue === true) {
        this.clublink.id = this.id
        this.loadClublink(this.clublink.id)
      }
    }
  },
  methods: {
    getFieldType (name) {
      return this.validationErrors.has(name) ? 'is-danger' : ''
    },
    getError (name) {
      return this.validationErrors.first(name)
    },
    loadClublink (id) {
      this.loadingError = ''
      this.isLoading = true
      this.canLeave = false
      const clublink = this.$store.state.clublinks.find((clublink) => {
        return clublink.id === id
      })
      if (clublink) {
        this.clublink = Object.assign(this.clublink, clublink)
        for (let i = 0; i < this.clublink.players.length; i++) {
          this.clublink.players[i].key = this.key
          this.key++
        }
        for (let i = 0; i < this.clublink.sponsors.length; i++) {
          this.clublink.sponsors[i].key = this.key
          this.key++
        }
      } else {
        this.loadingError = 'This clublink does not exist.'
      }
      this.isLoading = false
      this.originalClublink = cloneDeep(this.clublink)
    },
    addPlayer () {
      if (!this.playersLimitReached) {
        this.clublink.players.push({
          key: this.key,
          login: '',
          nickname: '',
          avatar: ''
        })
        this.key++
      }
    },
    removePlayer (key) {
      this.clublink.players = this.clublink.players.filter(player => player.key !== key)
    },
    addSponsor () {
      if (!this.sponsorsLimitReached) {
        this.clublink.sponsors.push({
          key: this.key,
          name: '',
          image: ''
        })
        this.key++
      }
    },
    removeSponsor (key) {
      this.clublink.sponsors = this.clublink.sponsors.filter(sponsor => sponsor.key !== key)
    },
    cancel () {
      this.$router.push({ name: routes.manage })
    },
    async create () {
      if (this.isLoading) return

      if (auth.currentUser === null) {
        this.errors.push('You must be connected to create a clublink.')
      }

      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.isLoading = true

      const createClublink = functions.httpsCallable('createClublink')
      createClublink(this.clublink).then((result) => {
        this.isLoading = false
        this.clublink.name = ''
        this.canLeave = true
        this.$router.push({ name: routes.manage })
      }).catch(err => {
        this.errors.push(err.message)
        this.isLoading = false
      })
    }
  }
}
</script>

<style>
.spacer {
  margin-top: 1em;
}
</style>
